"use client";

import { ChevronLeft } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Badge } from "@/shadcn-components/ui/badge";
import IndicatorConfigFactory from "./IndicatorConfigFactory";
import { getIndicatorDefinition } from "@/lib/chart-indicators/registry";

interface IndicatorConfigScreenProps {
  indicator: Record<string, unknown> & {
    indicatorId: string;
    name: string;
    createsNewChart?: boolean;
    settings?: { values?: Record<string, unknown> };
  };
  onBack: () => void;
  onApply: (indicator: unknown) => void;
  /** Explicit edit mode; settings.values is always populated so cannot be used to infer it. */
  isEditing?: boolean;
}

export default function IndicatorConfigScreen({
  indicator,
  onBack,
  onApply,
  isEditing = false,
}: IndicatorConfigScreenProps) {
  const def = getIndicatorDefinition(indicator.indicatorId);
  const paneLabel = indicator.createsNewChart
    ? "Separate pane"
    : "Main chart overlay";

  return (
    <div className="h-full bg-background text-foreground flex flex-col min-h-0">
      <div className="flex items-start gap-2 px-3 py-2.5 border-b border-border shrink-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="h-8 w-8 shrink-0 mt-0.5"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0 pr-1">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
            {isEditing ? "Edit indicator" : "New indicator"}
          </p>
          <h1 className="text-sm font-semibold truncate leading-tight">
            {indicator.name}
          </h1>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            <Badge variant="secondary" className="text-[10px] font-normal h-5">
              {paneLabel}
            </Badge>
            {def?.category ? (
              <Badge variant="outline" className="text-[10px] font-normal h-5 capitalize">
                {def.category.replace("-", " ")}
              </Badge>
            ) : null}
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        <IndicatorConfigFactory
          indicator={indicator}
          onBack={onBack}
          onApply={onApply}
          isEditing={isEditing}
        />
      </div>
    </div>
  );
}
